import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSplitScreenContext } from "./split-screen-context";
import { buildColorOptions } from "./split-screen.constants";
import { SplitModulePicker } from "./split-module-picker";
import { Select as PrimitiveSelect } from "radix-ui";
import { Outlet } from "react-router-dom";
import { HeaderBreadcrumbs } from "@/components/HeaderBreadcrumps";

export const SplitModuleWrapper = () => {
  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center">
        <HeaderBreadcrumbs />
        <SplitModuleControls />
      </div>
      <hr className="bg-black my-4 -mx-4" />
      <div className="flex-1 min-h-0 overflow-y-scroll py-2">
        <Outlet />
      </div>
    </div>
  );
};

const SplitModuleControls = () => {
  const { t } = useTranslation(["common"]);
  const { isModulePickerOpen, toggleModulePicker, stopSplitScreen } =
    useSplitScreenContext();

  return (
    <div className="ms-auto flex items-center gap-2">
      <Button
        variant="outline"
        onClick={toggleModulePicker}
        className="text-sm! bg-white"
      >
        <ArrowLeftRight className="text-black/50" />
        {t("common:chrome.switch")}
      </Button>
      <SplitPanelColorPicker />
      <Button
        title={t("common:a11y.close_split_screen")}
        variant="outline"
        className="bg-white px-2"
        onClick={stopSplitScreen}
      >
        <X size={20} />
      </Button>
      <SplitModulePicker
        isOpen={isModulePickerOpen}
        toggle={toggleModulePicker}
      />
    </div>
  );
};

const SplitPanelColorPicker = () => {
  const { t } = useTranslation(["common"]);
  const { highlightColor, setHighlightColor, splitPortalRef } =
    useSplitScreenContext();
  const handleSelect = (color: string) => {
    setHighlightColor(color);
  };

  return (
    <div
      data-tooltip-id="hrm-tooltip"
      data-tooltip-content={t("common:a11y.split_screen_highlight_color")}
      data-tooltip-place="bottom"
    >
      <Select onValueChange={handleSelect} defaultValue={highlightColor}>
        <SelectTrigger className="w-full min-w-40 h-10 bg-white">
          <SelectValue
            placeholder={t("common:a11y.select_a_highlight_color")}
          />
        </SelectTrigger>
        <PrimitiveSelect.Portal container={splitPortalRef.current}>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>{t("common:chrome.colors")}</SelectLabel>
              {buildColorOptions(t).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: option.value }}
                    />
                    {option.label}
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </PrimitiveSelect.Portal>
      </Select>
    </div>
  );
};
